import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Link,
} from '@mui/material';
import { ArrowBack as BackIcon, Edit as EditIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
function groupUsersList(group) {
  if (!group) return [];
  return group.users || group.Users || [];
}

function groupRolesList(group) {
  if (!group) return [];
  return group.roles || group.Roles || [];
}

/**
 * @param {object} props
 * @param {string} props.groupId
 * @param {'page'|'dialog'} props.variant
 * @param {() => void} [props.onRequestClose]
 */
const GroupDetailContent = ({ groupId, variant = 'page', onRequestClose }) => {
  const navigate = useNavigate();
  const { hasPermission, hasModuleAccess } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!groupId) {
      setLoading(false);
      setGroup(null);
      return undefined;
    }
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/groups/${groupId}`);
        if (!cancelled) setGroup(r.data.data);
      } catch {
        if (!cancelled) toast.error('Failed to load group');
        if (!cancelled && variant === 'page') navigate('/admin/groups');
        if (!cancelled && variant === 'dialog' && onRequestClose) onRequestClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId, variant, navigate, onRequestClose]);

  if (loading || !group) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: variant === 'dialog' ? 2 : 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const roles = [...groupRolesList(group)].sort((a, b) =>
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  );
  const members = [...groupUsersList(group)].sort((a, b) => {
    const la = `${a.last_name || ''} ${a.first_name || ''}`.trim();
    const lb = `${b.last_name || ''} ${b.first_name || ''}`.trim();
    return la.localeCompare(lb, undefined, { sensitivity: 'base' });
  });

  const detailInner = (
    <>
      <Card sx={{ mb: variant === 'dialog' ? 2 : 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Group details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {group.name || '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1">{group.is_active !== false ? 'Active' : 'Inactive'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1">{group.description || '—'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: variant === 'dialog' ? 2 : 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Roles ({roles.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {roles.map((r) => (
              <Chip key={r.id} label={r.name} size="small" color="primary" variant="outlined" />
            ))}
            {roles.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No roles assigned.
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Members ({members.length})
          </Typography>
          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No users in this group.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((u) => (
                    <TableRow key={u.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {u.first_name} {u.last_name}
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell align="right">
                        {hasModuleAccess('users') && (
                          <Link component={RouterLink} to={`/admin/users/${u.id}`} underline="hover">
                            View user
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </>
  );

  if (variant === 'dialog') {
    return detailInner;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/groups')}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, flex: 1 }}>
          View group
        </Typography>
        {hasPermission('groups_modify') && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/groups/${groupId}/edit`)}
            sx={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
          >
            Edit
          </Button>
        )}
      </Box>
      {detailInner}
    </Box>
  );
};

export default GroupDetailContent;
